import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Wallet, Users, Banknote, RefreshCw } from 'lucide-react';
import { listAllowances, getDepartments, type EmployeeAllowance } from '@/lib/hrApi';
import { Button } from '@/features/shared/components/ui/button';
import { getPayrollReadiness } from '@/services/payrollSettingsService';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/features/shared/components/ui/table';
import { Badge } from '@/features/shared/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/features/shared/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { EmployeeLine, Currency, PayComponent, LineStatus } from '../types';
import { formatMoney } from '../lib/format';
import { COMPONENT_KIND_LABEL, COMPONENT_KIND_ORDER, LINE_STATUS_META } from '../lib/state';
import { orderRunLines, countRunLines, hasPay, type RunLineCounts } from '../lib/runLines';
import { OneOffEarningDialog } from './OneOffEarningDialog';

interface EmployeesTabProps {
  employees: EmployeeLine[];
  currency: Currency;
  periodMonth?: string; // YYYY-MM — enables "Add one-off earning" when editable
  canEdit?: boolean;
  /** Set when the Employees read FAILED (backend message): show that, with
   *  Retry — never the empty-run banner, never the one-off earning picker. */
  unavailable?: string;
  onRetry?: () => void;
  retrying?: boolean;
}

export const EmployeesTab: React.FC<EmployeesTabProps> = ({
  employees, currency, periodMonth, canEdit, unavailable, onRetry, retrying,
}) => {
  const [selected, setSelected] = useState<EmployeeLine | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // Resolve department NAMES: the payroll snapshot only freezes the department_id;
  // names live in employee-service, so map id → name for display.
  const [deptNames, setDeptNames] = useState<Record<string, string>>({});
  useEffect(() => {
    let live = true;
    getDepartments()
      .then((res: any) => {
        if (!live) return;
        const items: any[] = res?.items ?? res?.results ?? (Array.isArray(res) ? res : []);
        const map: Record<string, string> = {};
        for (const d of items) if (d?.id && d?.name) map[String(d.id)] = String(d.name);
        setDeptNames(map);
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  const deptLabel = (e: EmployeeLine) =>
    (e.departmentId && deptNames[e.departmentId]) || e.department || '—';
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // /t/:slug/app/payroll/runs/:id → /t/:slug/app/payroll/settings
  const settingsBase = pathname.replace(/\/runs\/[^/]+\/?$/, '/settings');
  const openAllowances = (employeeId: string) =>
    navigate(`${settingsBase}?tab=employees&employee=${employeeId}`);

  // A failed read is "we do not know", not "nobody": say so, offer Retry, and
  // claim nothing about the run's population (DEF-0mvnq1snl001-4c48d4).
  if (unavailable !== undefined) {
    return <EmployeesUnavailable message={unavailable} onRetry={onRetry} retrying={retrying} />;
  }

  // An empty run almost always means employees aren't set up for payroll in this
  // company (no legal entity + salary) — a silent "0" is a dead-end, so explain it
  // and link to the fix instead of showing an empty table.
  if (employees.length === 0) {
    return <EmptyRunBanner />;
  }

  // Every line of the run, problems first: an errored or excluded person has no
  // calculation, so they get a badge + the reason and "—" for money — never a
  // zero that reads as a paid line (DEF-0mvnmww1b001-1cc65a).
  const ordered = orderRunLines(employees);
  const counts = countRunLines(employees);
  const calculatedLines = employees.filter(hasPay);

  return (
    <>
      {counts.notCalculated > 0 && <NotCalculatedSummary counts={counts} />}
      {canEdit && periodMonth && (
        <div className="mb-3 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
            <Banknote className="mr-1.5 h-4 w-4" /> Add bonus / commission
          </Button>
        </div>
      )}
      {addOpen && periodMonth && (
        <OneOffEarningDialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          employees={calculatedLines.map((e) => ({ employeeId: e.employeeId, fullName: e.fullName }))}
          periodMonth={periodMonth}
          currency={String(currency)}
        />
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Employee</TableHead>
            <TableHead>Department</TableHead>
            <TableHead className="text-right">Gross</TableHead>
            <TableHead className="text-right">Deductions</TableHead>
            <TableHead className="text-right">Net</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {ordered.map((e, i) => {
            const paid = hasPay(e);
            const meta = paid ? null : LINE_STATUS_META[e.status as Exclude<LineStatus, 'calculated'>];
            return (
              <TableRow
                key={`${e.employeeId}-${e.status}-${i}`}
                data-line-status={e.status}
                onClick={() => paid && setSelected(e)}
                className={cn(paid && 'cursor-pointer', e.status === 'error' && 'bg-danger-soft')}
              >
                <TableCell>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground">{e.fullName}</span>
                    {meta && <Badge variant={meta.variant}>{meta.label}</Badge>}
                  </div>
                  {!paid && (
                    <div
                      className={cn(
                        'mt-0.5 flex items-start gap-1 text-xs font-medium',
                        e.status === 'error' ? 'text-danger-strong' : 'text-muted-foreground',
                      )}
                    >
                      <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span>{e.errorReason || NO_REASON[e.status as Exclude<LineStatus, 'calculated'>]}</span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{deptLabel(e)}</TableCell>
                {paid ? (
                  <>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {formatMoney(e.gross, currency)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-danger-strong">
                      −{formatMoney(e.employeeDeductions, currency)}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums text-foreground">
                      {formatMoney(e.net, currency)}
                    </TableCell>
                  </>
                ) : (
                  <>
                    {[0, 1, 2].map((k) => (
                      <TableCell key={k} className="text-right text-muted-foreground">
                        <span aria-label="No calculation">—</span>
                      </TableCell>
                    ))}
                  </>
                )}
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      title="Manage allowances"
                      onClick={(ev) => { ev.stopPropagation(); openAllowances(e.employeeId); }}
                      className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                      <Wallet className="h-4 w-4" />
                    </button>
                    {paid && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <LineDetailDialog line={selected} currency={currency} onClose={() => setSelected(null)} />
    </>
  );
};

// EmployeesUnavailable — the run's Employees read failed. Shows the backend
// message and a Retry; deliberately NOT the empty-run banner.
const EmployeesUnavailable: React.FC<{ message: string; onRetry?: () => void; retrying?: boolean }> = ({
  message, onRetry, retrying,
}) => (
  <div role="alert" className="rounded-lg border border-danger bg-danger-soft p-4 text-sm text-danger-strong">
    <p className="flex items-center gap-2 font-semibold">
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
      The employee list for this run could not be loaded
    </p>
    <p className="mt-1">{message || 'Please try again.'}</p>
    <p className="mt-1">
      This does not mean the run has no employees. Headcount and the list are unknown until it loads.
    </p>
    {onRetry && (
      <Button variant="outline" size="sm" className="mt-3 gap-2" disabled={retrying} onClick={onRetry}>
        <RefreshCw className={cn('h-4 w-4', retrying && 'animate-spin')} aria-hidden="true" /> Retry
      </Button>
    )}
  </div>
);

// When the server sent no reason the line still must not look fine.
const NO_REASON: Record<Exclude<LineStatus, 'calculated'>, string> = {
  error: 'Could not be calculated — no reason was recorded',
  excluded: 'Not paid by this run — no reason was recorded',
  pending: 'Not calculated yet — run Calculate',
};

// NotCalculatedSummary — above the table whenever some lines have no
// calculation: how many, and that they are not in the run totals.
const NotCalculatedSummary: React.FC<{ counts: RunLineCounts }> = ({ counts }) => {
  const parts = [
    counts.error > 0 && `${counts.error} errored`,
    counts.excluded > 0 && `${counts.excluded} excluded`,
    counts.pending > 0 && `${counts.pending} not calculated yet`,
  ].filter(Boolean);
  const tone = counts.error > 0 ? 'border-danger bg-danger-soft text-danger-strong' : 'border-warning bg-warning-soft text-warning-strong';
  return (
    <div role="status" className={cn('mb-3 flex items-start gap-2 rounded-md border px-3 py-2 text-sm', tone)}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <p>
        <span className="font-semibold">
          {counts.notCalculated} of {counts.calculated + counts.notCalculated} employee
          {counts.calculated + counts.notCalculated === 1 ? '' : 's'} not paid by this run
        </span>{' '}
        ({parts.join(', ')}). They are listed first with the reason and are not in the run totals — fix the
        reason, then calculate again.
      </p>
    </div>
  );
};

// EmptyRunBanner — shown when a run has no employee lines. Explains WHY (setup gap)
// and links to Bulk Salaries, with a live count of employees still needing setup.
const EmptyRunBanner: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const salariesPath = pathname.replace(/\/runs\/[^/]+\/?$/, '/salaries');
  const [needsSetup, setNeedsSetup] = useState<number | null>(null);
  const [total, setTotal] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    getPayrollReadiness()
      .then((r) => {
        if (!live) return;
        setTotal(r.summary.total);
        setNeedsSetup(Math.max(0, r.summary.total - r.summary.ready));
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  return (
    <div className="rounded-lg border border-amber-300/60 bg-amber-500/10 p-6 text-center">
      <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-amber-500/20">
        <Users className="h-5 w-5 text-amber-600" />
      </div>
      <h3 className="text-base font-semibold text-charcoal dark:text-foreground">No employees in this run yet</h3>
      <p className="mx-auto mt-1.5 max-w-xl text-sm text-muted-foreground">
        {total !== null && total > 0 && needsSetup ? (
          <>
            You have <strong>{needsSetup}</strong> employee{needsSetup === 1 ? '' : 's'} who still need to be
            assigned to this company (legal entity) and given a salary before they appear in payroll.
          </>
        ) : (
          <>Employees must be assigned to this company (legal entity) and given a salary before they appear in a payroll run.</>
        )}
      </p>
      <div className="mt-4 flex items-center justify-center">
        <Button onClick={() => navigate(salariesPath)}>
          <Banknote className="mr-2 h-4 w-4" /> Set up employees (Bulk Salaries)
        </Button>
      </div>
      <p className="mx-auto mt-2 max-w-xl text-xs text-muted-foreground">
        Bulk Salaries assigns the legal entity and sets base pay in one upload. After setup, validate the run
        to include them.
      </p>
    </div>
  );
};

interface LineDetailDialogProps {
  line: EmployeeLine | null;
  currency: Currency;
  onClose: () => void;
}

const LineDetailDialog: React.FC<LineDetailDialogProps> = ({ line, currency, onClose }) => {
  const [allowances, setAllowances] = useState<EmployeeAllowance[] | null>(null);

  useEffect(() => {
    if (!line?.employeeId) { setAllowances(null); return; }
    let live = true;
    setAllowances(null);
    listAllowances(line.employeeId)
      .then((r) => { if (live) setAllowances(r); })
      .catch(() => { if (live) setAllowances([]); }); // disabled/none → empty
    return () => { live = false; };
  }, [line?.employeeId]);

  return (
    <Dialog open={!!line} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        {line && (
          <>
            <DialogHeader>
              <DialogTitle>{line.fullName}</DialogTitle>
              <DialogDescription>
                {line.department} · Net {formatMoney(line.net, currency)}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {COMPONENT_KIND_ORDER.map((kind) => {
                const items = line.components.filter((c) => c.kind === kind);
                if (items.length === 0) return null;
                return (
                  <div key={kind}>
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {COMPONENT_KIND_LABEL[kind]}
                    </p>
                    <div className="space-y-1">
                      {items.map((c) => (
                        <ComponentRow key={c.code} c={c} currency={currency} />
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Allowances configured for this employee (Benefits service). These are
                  what's on file; only approved ones flow into the run above. */}
              {allowances && allowances.length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Allowances on file
                  </p>
                  <div className="space-y-1">
                    {allowances.map((a) => (
                      <div key={a.id} className="flex items-center justify-between text-sm">
                        <span className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
                          {a.name}
                          {a.in_kind && (
                            <Badge variant="outline" className="px-1.5 py-0 text-[10px]">in kind</Badge>
                          )}
                          {a.status !== 'approved' && a.status !== 'active' && (
                            <Badge variant="outline" className="px-1.5 py-0 text-[10px] capitalize">{a.status}</Badge>
                          )}
                        </span>
                        <span className="tabular-nums text-charcoal">
                          {formatMoney(a.amount, currency)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between border-t border-border pt-3">
                <span className="text-sm font-semibold text-charcoal">Net pay</span>
                <span className="text-h3 text-charcoal tabular-nums">{formatMoney(line.net, currency)}</span>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ComponentRow: React.FC<{ c: PayComponent; currency: Currency }> = ({ c, currency }) => {
  const negative = c.amount < 0;
  const employer = c.kind === 'employer';
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        {c.label}
        {c.statutory && <Badge variant="outline" className="px-1.5 py-0 text-[10px]">statutory</Badge>}
      </span>
      <span
        className={cn(
          'tabular-nums',
          employer ? 'text-muted-foreground' : negative ? 'text-[#991B1B]' : 'text-charcoal',
        )}
      >
        {negative ? '−' : employer ? '' : '+'}
        {formatMoney(Math.abs(c.amount), currency)}
      </span>
    </div>
  );
};
